import { supabase } from '../lib/supabase';

/**
 * User Profile Service
 * Comprehensive service for user profile analytics and data management
 */

// Get complete user profile with all related data
export const getUserProfile = async (userId) => {
  try {
    // Fetch user details from profiles table (without complex join)
    const { data: user, error: userError } = await supabase
      .from('profiles')
      .select(`
        id,
        first_name,
        last_name,
        full_name,
        email,
        role,
        coach_id,
        avatar_url,
        created_at,
        updated_at
      `)
      .eq('id', userId)
      .single();

    if (userError) {
      throw new Error(`Failed to fetch user: ${userError.message}`);
    }

    // Fetch coach details separately if coach_id exists
    let coach = null;
    if (user.coach_id) {
      const { data: coachData, error: coachError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, email')
        .eq('id', user.coach_id)
        .single();

      if (!coachError && coachData) {
        coach = coachData;
      }
    }

    // Attach coach data to user
    const userWithCoach = { ...user, coach };

    // Fetch call statistics
    const { data: callStatsData, error: callStatsError } = await supabase
      .rpc('get_user_call_stats', { target_user_id: userId });

    if (callStatsError) {
      console.warn('Failed to fetch call stats:', callStatsError);
    }

    // Fetch memory statistics
    const { data: memoryStatsData, error: memoryStatsError } = await supabase
      .rpc('get_user_memory_stats', { target_user_id: userId });

    if (memoryStatsError) {
      console.warn('Failed to fetch memory stats:', memoryStatsError);
    }

    // Fetch recent activity timeline
    const { data: timelineData, error: timelineError } = await supabase
      .rpc('get_user_activity_timeline', { 
        target_user_id: userId, 
        limit_count: 20 
      });

    if (timelineError) {
      console.warn('Failed to fetch activity timeline:', timelineError);
    }

    return {
      user: userWithCoach,
      callStats: callStatsData || getDefaultCallStats(),
      memoryStats: memoryStatsData || getDefaultMemoryStats(),
      timeline: timelineData || [],
      error: null
    };

  } catch (error) {
    console.error('Error fetching user profile:', error);
    return {
      user: null,
      callStats: getDefaultCallStats(),
      memoryStats: getDefaultMemoryStats(),
      timeline: [],
      error: error.message
    };
  }
};

// Get user call statistics
export const getUserCallStats = async (userId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_call_stats', { target_user_id: userId });

    if (error) {
      throw new Error(`Failed to fetch call stats: ${error.message}`);
    }

    return { data: data || getDefaultCallStats(), error: null };
  } catch (error) {
    console.error('Error fetching call stats:', error);
    return { data: getDefaultCallStats(), error: error.message };
  }
};

// Get user memory statistics
export const getUserMemoryStats = async (userId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_memory_stats', { target_user_id: userId });

    if (error) {
      throw new Error(`Failed to fetch memory stats: ${error.message}`);
    }

    return { data: data || getDefaultMemoryStats(), error: null };
  } catch (error) {
    console.error('Error fetching memory stats:', error);
    return { data: getDefaultMemoryStats(), error: error.message };
  }
};

// Get user activity timeline
export const getUserActivityTimeline = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_activity_timeline', { 
        target_user_id: userId, 
        limit_count: limit 
      });

    if (error) {
      throw new Error(`Failed to fetch activity timeline: ${error.message}`);
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching activity timeline:', error);
    return { data: [], error: error.message };
  }
};

// Default stats objects for fallback
const getDefaultCallStats = () => ({
  total_calls: 0,
  first_call_date: null,
  last_call_date: null,
  avg_duration_minutes: 0,
  total_duration_minutes: 0,
  success_rate: 0,
  avg_cost: 0,
  total_cost: 0,
  avg_model_latency: 0,
  avg_voice_latency: 0,
  calls_this_week: 0,
  calls_this_month: 0,
  avg_tokens_per_call: 0
});

const getDefaultMemoryStats = () => ({
  total_memories: 0,
  categories_used: 0,
  latest_memory_date: null,
  oldest_memory_date: null,
  memories_this_week: 0,
  memories_this_month: 0,
  avg_memories_per_week: 0,
  category_breakdown: {}
});

// Format user display name
export const formatUserName = (user) => {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.full_name || user.email || 'Unknown User';
};

// Format coach display name
export const formatCoachName = (coach) => {
  if (!coach) return 'None';
  if (coach.first_name && coach.last_name) {
    return `${coach.first_name} ${coach.last_name}`;
  }
  return coach.full_name || coach.email || 'Unknown Coach';
};

// Get user role display name
export const formatRole = (role) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'COACH':
      return 'Coach';
    case 'CLIENT':
      return 'Client';
    default:
      return role;
  }
};

// Get role color classes
export const getRoleColorClasses = (role) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'COACH':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'CLIENT':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Calculate days since last activity
export const daysSinceLastActivity = (lastCallDate, lastMemoryDate) => {
  const dates = [lastCallDate, lastMemoryDate].filter(Boolean);
  if (dates.length === 0) return null;
  
  const mostRecent = new Date(Math.max(...dates.map(d => new Date(d))));
  const now = new Date();
  const diffTime = Math.abs(now - mostRecent);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

// Get engagement level based on activity
export const getEngagementLevel = (callStats, memoryStats) => {
  const recentCalls = callStats.calls_this_week || 0;
  const recentMemories = memoryStats.memories_this_week || 0;
  const totalActivity = recentCalls + recentMemories;
  
  if (totalActivity >= 5) return { level: 'high', color: 'green', label: 'High' };
  if (totalActivity >= 2) return { level: 'medium', color: 'yellow', label: 'Medium' };
  if (totalActivity >= 1) return { level: 'low', color: 'orange', label: 'Low' };
  return { level: 'inactive', color: 'red', label: 'Inactive' };
};